import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, type ColumnDef } from "@/components/table";
import { PATHS } from "@/routes/paths";
import { useLocale } from "@/hooks/useLocale";
import { Component, User, Users } from "lucide-react";
import { useState } from "react";
import { PROJECT_TAB_KEYS } from "@/routes/company/routes.projects.constants";
import { useProjectTabParams } from "../params";
import { useParams } from "react-router-dom";
import { useGetProjectTeamsByProjectIdQuery } from "@/services/(company)/api.projects";
import type { projectIdKey } from "@/routes/company/routes.projects.constants";
import { cn } from "@/utils/cn";
import { TeamDetailsDrawer } from "../../../teams/_components/team-details-drawer";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";

const buildColumns = (t: TFunction): ColumnDef<Record<string, unknown>>[] => [
  {
    key: "name",
    label: t("projects:team_name"),
  },
  {
    key: "manager",
    label: t("projects:manager"),
  },
  {
    key: "members",
    label: t("projects:members"),
  },
  {
    key: "createdAt",
    label: t("projects:created_date"),
  },
];

export const ProjectTeamsTable = () => {
  const { t } = useTranslation(["projects", "common"]);
  const { formatDate } = useLocale();
  const { projectId } = useParams<{ [projectIdKey]: string }>();
  const { can } = usePermissions();
  // Search and page live in the URL, namespaced per tab — see useTableParams.
  const tp = useProjectTabParams(PROJECT_TAB_KEYS.Team);

  const { data: { meta, data: projects } = {}, isFetching } =
    useGetProjectTeamsByProjectIdQuery(
      {
        projectId: projectId!,
        params: { page: tp.page, limit: 10, search: tp.committedSearch },
      },
      { skip: !projectId },
    );

  // Teams details drawer related
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [isTeamDetailsDrawerOpen, setIsTeamDetailsDrawerOpen] = useState(false);

  const handleOpenDrawer = (teamId: number) => {
    setSelectedTeamId(teamId);
    setIsTeamDetailsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setSelectedTeamId(null);
    setIsTeamDetailsDrawerOpen(false);
  };

  const rows =
    projects?.map((team) => ({
      id: team.id,
      name: (
        <div className="flex items-center gap-2">
          <Avatar>
            <AvatarImage src={team.image ?? undefined} />
            <AvatarFallback>
              <Component />
            </AvatarFallback>
          </Avatar>
          {team.name}
        </div>
      ),
      manager: (
        <div className="flex items-center">
          {team?.managers?.slice(0, 2).map((manager, i) => (
            <Avatar
              key={manager.id}
              className={`${i > 0 ? "-ml-4" : ""} border-2 border-white`}
            >
              <AvatarImage src={manager?.user?.profileImage ?? undefined} />
              <AvatarFallback>
                {manager?.user?.fullName.charAt(0) || <User size={20} />}
              </AvatarFallback>
            </Avatar>
          ))}
          {team?.managers?.length > 2 && (
            <span className="ms-2 font-semibold text-primary">
              {t("projects:others_count", { count: team?.managers.length - 2 })}
            </span>
          )}
          {(!team?.managers || team?.managers.length === 0) &&
            t("projects:no_managers")}
        </div>
      ),
      members: (
        <div className="flex items-center">
          {team?.members?.slice(0, 2).map((member, i) => (
            <Avatar
              key={member.id}
              className={cn("border-2 border-white", i > 0 && "-ml-4")}
            >
              <AvatarImage src={member?.user?.profileImage ?? undefined} />
              <AvatarFallback>
                {member?.user?.fullName?.charAt(0) || <User size={20} />}
              </AvatarFallback>
            </Avatar>
          ))}
          {(!team?.members || team.members.length === 0) &&
            t("projects:no_members")}
        </div>
      ),
      createdAt: formatDate(new Date(team.createdAt), "dd MMM yyyy"),
    })) ?? [];

  if (!projectId) return <p>{t("projects:project_not_found")}</p>;
  return (
    <>
      <Table
        data={rows}
        loading={isFetching}
        searchText={tp.search}
        columns={buildColumns(t)}
        noPadding
        pagination={{
          totalPages: meta?.totalPages ?? 1,
          currentPage: tp.page,
          onPageChange: tp.setPage,
        }}
        onSearchChange={tp.setSearch}
        searchPlaceholder={t("projects:search_by_team_name")}
        actions={[
          ...(can(PLATFORM_SECTION_ENUM.PROJECTS, "edit")
            ? [
                {
                  type: "link" as const,
                  key: "add",
                  label: t("projects:manage_team_s"),
                  icon: <Users className="size-4" />,
                  variant: "primary" as const,
                  href: PATHS.COMPANY.COMPANY_PROJECTS_PATHS.ADD_TEAM(
                    projectId,
                  ),
                },
              ]
            : []),
        ]}
        onRowClick={(row) => handleOpenDrawer(row.id as number)}
      />
      <TeamDetailsDrawer
        isOpen={isTeamDetailsDrawerOpen}
        teamId={selectedTeamId}
        onClose={handleCloseDrawer}
      />
    </>
  );
};
